import React, { useState, useEffect } from "react";
import {
  useGetAccountQuery,
  useUpdatePreferencesMutation,
} from "../../../api/account";
import AccountSectionEditInPlaceWrapper from "./AccountSectionEditInPlaceWrapper";
import LoadingPlaceholder from "../loading/LoadingPlaceholder";
import ApiResponseModal from "../popup/ApiResponseModal";

const AvailabilityContent = () => {
  // Removed modal states since we're using edit-in-place mode
  const [isInitialLoading, setIsInitialLoading] = useState(true);

  // API hooks
  const { data: accountInfo, isSuccess: isAccountLoaded } =
    useGetAccountQuery();
  const [updatePreferences] = useUpdatePreferencesMutation();

  // Edit mode states for each section
  const [isHoursEditMode, setIsHoursEditMode] = useState(false);
  const [isDaysEditMode, setIsDaysEditMode] = useState(false);

  // Hours selection state
  const [hoursSelection, setHoursSelection] = useState({
    morning: false,
    afternoon: false,
    evening: false,
    anytime: true,
  });

  // Track original state for Hours
  const [originalHoursSelection, setOriginalHoursSelection] = useState({
    morning: false,
    afternoon: false,
    evening: false,
    anytime: true,
  });

  // Days selection state
  const [daysSelection, setDaysSelection] = useState({
    workingDays: false,
    weekends: false,
    allWeek: true,
  });

  // Track original state for Days
  const [originalDaysSelection, setOriginalDaysSelection] = useState({
    workingDays: false,
    weekends: false,
    allWeek: true,
  });

  // Track if we've already initialized from API
  const [hasInitialized, setHasInitialized] = useState(false);

  // Modal states for preferences update
  const [showPreferencesModal, setShowPreferencesModal] = useState(false);
  const [preferencesModalMessage, setPreferencesModalMessage] = useState("");
  const [isPreferencesSuccess, setIsPreferencesSuccess] = useState(false);
  const [isPreferencesLoading, setIsPreferencesLoading] = useState(false);
  const [currentSection, setCurrentSection] = useState(null);
  const [currentPreferencesData, setCurrentPreferencesData] = useState(null);

  // Initialize state from API data (only once)
  useEffect(() => {
    if (
      isAccountLoaded &&
      accountInfo?.preferences?.availability &&
      !hasInitialized
    ) {
      const availabilityData = accountInfo.preferences.availability;

      // Initialize hours
      if (availabilityData.hours) {
        const hours = availabilityData.hours;
        const newHoursSelection = {
          morning: hours.includes("morning"),
          afternoon: hours.includes("afternoon"),
          evening: hours.includes("evening"),
          anytime: hours.includes("anytime"),
        };
        setHoursSelection(newHoursSelection);
        setOriginalHoursSelection(newHoursSelection);
      }

      // Initialize days
      if (availabilityData.days) {
        const days = availabilityData.days;
        const newDaysSelection = {
          workingDays: days.includes("workingDays"),
          weekends: days.includes("weekends"),
          allWeek: days.includes("allWeek"),
        };
        setDaysSelection(newDaysSelection);
        setOriginalDaysSelection(newDaysSelection);
      }

      setHasInitialized(true);
    }
  }, [isAccountLoaded, accountInfo, hasInitialized]);

  // Check if changes have been made
  const hasHoursChanges =
    JSON.stringify(hoursSelection) !== JSON.stringify(originalHoursSelection);
  const hasDaysChanges =
    JSON.stringify(daysSelection) !== JSON.stringify(originalDaysSelection);

  // Handle hours checkbox changes with automatic "Anytime" logic
  const handleHoursChange = (field, checked) => {
    setHoursSelection((prev) => {
      const newSelection = { ...prev, [field]: checked };

      // If "Anytime" is checked, uncheck all others
      if (field === "anytime" && checked) {
        newSelection.morning = false;
        newSelection.afternoon = false;
        newSelection.evening = false;
      }
      // If all specific times are checked, check "Anytime" and uncheck them
      else if (
        field === "morning" &&
        checked &&
        prev.afternoon &&
        prev.evening
      ) {
        newSelection.anytime = true;
        newSelection.morning = false;
        newSelection.afternoon = false;
        newSelection.evening = false;
      } else if (
        field === "afternoon" &&
        checked &&
        prev.morning &&
        prev.evening
      ) {
        newSelection.anytime = true;
        newSelection.morning = false;
        newSelection.afternoon = false;
        newSelection.evening = false;
      } else if (
        field === "evening" &&
        checked &&
        prev.morning &&
        prev.afternoon
      ) {
        newSelection.anytime = true;
        newSelection.morning = false;
        newSelection.afternoon = false;
        newSelection.evening = false;
      }
      // If only one specific time is checked, uncheck "Anytime"
      else if (
        (field === "morning" && checked && !prev.afternoon && !prev.evening) ||
        (field === "afternoon" && checked && !prev.morning && !prev.evening) ||
        (field === "evening" && checked && !prev.morning && !prev.afternoon)
      ) {
        newSelection.anytime = false;
      }
      // If unchecking specific times, and "Anytime" was checked, uncheck "Anytime"
      else if (
        (field === "morning" && !checked) ||
        (field === "afternoon" && !checked) ||
        (field === "evening" && !checked)
      ) {
        newSelection.anytime = false;
      }

      return newSelection;
    });
  };

  // Handle days checkbox changes with automatic "All Week" logic
  const handleDaysChange = (field, checked) => {
    setDaysSelection((prev) => {
      const newSelection = { ...prev, [field]: checked };

      // If "All Week" is checked, uncheck others
      if (field === "allWeek" && checked) {
        newSelection.workingDays = false;
        newSelection.weekends = false;
      }
      // If both Working Days and Weekends are checked, check "All Week" and uncheck them
      else if (field === "workingDays" && checked && prev.weekends) {
        newSelection.allWeek = true;
        newSelection.workingDays = false;
        newSelection.weekends = false;
      } else if (field === "weekends" && checked && prev.workingDays) {
        newSelection.allWeek = true;
        newSelection.workingDays = false;
        newSelection.weekends = false;
      }
      // If only one is checked, uncheck "All Week"
      else if (
        (field === "workingDays" && checked && !prev.weekends) ||
        (field === "weekends" && checked && !prev.workingDays)
      ) {
        newSelection.allWeek = false;
      }
      // If unchecking, and "All Week" was checked, uncheck "All Week"
      else if (
        (field === "workingDays" && !checked) ||
        (field === "weekends" && !checked)
      ) {
        newSelection.allWeek = false;
      }

      return newSelection;
    });
  };

  // Modal handlers
  const handlePreferencesUpdate = async (section, preferencesData) => {
    setCurrentSection(section);
    setCurrentPreferencesData(preferencesData);
    setShowPreferencesModal(true);
    setIsPreferencesLoading(true);
    setIsPreferencesSuccess(false);

    try {
      const result = await updatePreferences({
        section: section,
        preferences: preferencesData,
      }).unwrap();

      setIsPreferencesLoading(false);
      setPreferencesModalMessage(
        "Your information has been updated successfully."
      );
      setIsPreferencesSuccess(true);
    } catch (error) {
      setIsPreferencesLoading(false);
      setPreferencesModalMessage(
        "We couldn’t update your information. Please try again."
      );
      setIsPreferencesSuccess(false);
    }
  };

  const handleClosePreferencesModal = () => {
    setShowPreferencesModal(false);
    if (isPreferencesSuccess) {
      // Reset original state to current state after successful save and exit edit mode
      if (currentSection === "availability") {
        // Update the appropriate original state and exit edit mode
        setOriginalHoursSelection(hoursSelection);
        setOriginalDaysSelection(daysSelection);
        setIsHoursEditMode(false);
        setIsDaysEditMode(false);
      }
    }
  };

  const handleRetryPreferences = () => {
    if (currentSection && currentPreferencesData) {
      handlePreferencesUpdate(currentSection, currentPreferencesData);
    }
  };

  // Simulate brief loading for consistency and future API calls
  useEffect(() => {
    const timer = setTimeout(() => setIsInitialLoading(false), 500);
    return () => clearTimeout(timer);
  }, []);

  if (isInitialLoading) {
    return <LoadingPlaceholder rows={2} type="card-large" />;
  }

  return (
    <>
      <AccountSectionEditInPlaceWrapper
        title="Hours"
        isEditMode={isHoursEditMode}
        onToggleEdit={() => setIsHoursEditMode(!isHoursEditMode)}
        hasChanges={hasHoursChanges}
        onApplyChanges={() => {
          // Call the modal handler with the preferences data
          handlePreferencesUpdate("availability", {
            hours: Object.keys(hoursSelection).filter(
              (key) => hoursSelection[key]
            ),
          });
        }}
        onDiscardChanges={() => {
          setHoursSelection(originalHoursSelection);
        }}
        infoTooltip="Set your preferred working hours. Smart Match will prioritize your schedule whenever possible."
        section="availability"
        preferencesData={{
          hours: Object.keys(hoursSelection).filter(
            (key) => hoursSelection[key]
          ),
        }}
      >
        <div className="form-field">
          <div className="checkbox-container">
            <div className="checkbox-item">
              <div className="checkbox-representation">
                <input
                  type="checkbox"
                  id="morning"
                  checked={hoursSelection.morning}
                  onChange={(e) =>
                    handleHoursChange("morning", e.target.checked)
                  }
                  disabled={!isHoursEditMode}
                />
                <label htmlFor="morning">Morning</label>
              </div>
            </div>
            <div className="checkbox-item">
              <div className="checkbox-representation">
                <input
                  type="checkbox"
                  id="afternoon"
                  checked={hoursSelection.afternoon}
                  onChange={(e) =>
                    handleHoursChange("afternoon", e.target.checked)
                  }
                  disabled={!isHoursEditMode}
                />
                <label htmlFor="afternoon">Afternoon</label>
              </div>
            </div>
            <div className="checkbox-item">
              <div className="checkbox-representation">
                <input
                  type="checkbox"
                  id="evening"
                  checked={hoursSelection.evening}
                  onChange={(e) =>
                    handleHoursChange("evening", e.target.checked)
                  }
                  disabled={!isHoursEditMode}
                />
                <label htmlFor="evening">Evening</label>
              </div>
            </div>
            <div className="checkbox-item">
              <div className="checkbox-representation">
                <input
                  type="checkbox"
                  id="anytime"
                  checked={hoursSelection.anytime}
                  onChange={(e) =>
                    handleHoursChange("anytime", e.target.checked)
                  }
                  disabled={!isHoursEditMode}
                />
                <label htmlFor="anytime">Anytime</label>
              </div>
            </div>
          </div>
        </div>
      </AccountSectionEditInPlaceWrapper>

      <AccountSectionEditInPlaceWrapper
        title="Days"
        isEditMode={isDaysEditMode}
        onToggleEdit={() => setIsDaysEditMode(!isDaysEditMode)}
        hasChanges={hasDaysChanges}
        onApplyChanges={() => {
          // Call the modal handler with the preferences data
          handlePreferencesUpdate("availability", {
            days: Object.keys(daysSelection).filter(
              (key) => daysSelection[key]
            ),
          });
        }}
        onDiscardChanges={() => {
          setDaysSelection(originalDaysSelection);
        }}
        infoTooltip="Select which days you prefer to work. Smart Match will prioritize your availability when assigning new clients."
        section="availability"
        preferencesData={{
          days: Object.keys(daysSelection).filter((key) => daysSelection[key]),
        }}
      >
        <div className="form-field">
          <div className="checkbox-container">
            <div className="checkbox-item">
              <div className="checkbox-representation">
                <input
                  type="checkbox"
                  id="workingDays"
                  checked={daysSelection.workingDays}
                  onChange={(e) =>
                    handleDaysChange("workingDays", e.target.checked)
                  }
                  disabled={!isDaysEditMode}
                />
                <label htmlFor="workingDays">Only Working Days</label>
              </div>
            </div>
            <div className="checkbox-item">
              <div className="checkbox-representation">
                <input
                  type="checkbox"
                  id="weekends"
                  checked={daysSelection.weekends}
                  onChange={(e) =>
                    handleDaysChange("weekends", e.target.checked)
                  }
                  disabled={!isDaysEditMode}
                />
                <label htmlFor="weekends">Only Weekends</label>
              </div>
            </div>
            <div className="checkbox-item">
              <div className="checkbox-representation">
                <input
                  type="checkbox"
                  id="allWeek"
                  checked={daysSelection.allWeek}
                  onChange={(e) =>
                    handleDaysChange("allWeek", e.target.checked)
                  }
                  disabled={!isDaysEditMode}
                />
                <label htmlFor="allWeek">All Week</label>
              </div>
            </div>
          </div>
        </div>
      </AccountSectionEditInPlaceWrapper>

      {/* Preferences Update Modal */}
      <ApiResponseModal
        isOpen={showPreferencesModal}
        isLoading={isPreferencesLoading}
        isSuccess={isPreferencesSuccess}
        message={preferencesModalMessage}
        onClose={handleClosePreferencesModal}
        onRetry={handleRetryPreferences}
      />
    </>
  );
};

export default AvailabilityContent;
